import type { SubScene } from "@/api/solutionApi";
import { assetUrl } from "@/utils/assetUrl";

interface Props {
  scene: SubScene;
}

function getFeatureGridClass(count: number): string {
  const base = "grid grid-cols-1 sm:grid-cols-2 gap-4 ";
  if (count === 1) return base + "lg:grid-cols-1";
  if (count === 2) return base + "lg:grid-cols-2";
  if (count === 3) return base + "lg:grid-cols-3";
  if (count === 4) return base + "lg:grid-cols-4";
  if (count <= 6) return base + "lg:grid-cols-3";
  return base + "lg:grid-cols-4";
}

export default function DetailIntro({ scene }: Props) {
  const gridClass = getFeatureGridClass(scene.features.length);

  return (
    <section className="py-16 lg:py-20 bg-white">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        {/* Section header */}
        <div className="flex items-center gap-3 mb-10">
          <div className="w-1 h-6 rounded-full" style={{ backgroundColor: scene.accentColor }}></div>
          <div>
            <p className="text-xs text-gray-400 font-medium tracking-wider uppercase mb-0.5">Solution Brief</p>
            <h2 className="text-xl font-bold text-[#0d1f3c]">方案简介</h2>
          </div>
        </div>

        {/* Top: introduction text + tags */}
        <div className="mb-10">
          <p className="text-gray-600 text-sm leading-relaxed mb-6">
            {scene.introduction}
          </p>
          <div className="flex flex-wrap gap-2">
            {(scene.introTags || scene.tags).map((tag) => (
              <span
                key={tag}
                className="px-3 py-1.5 text-xs font-medium rounded-full border whitespace-nowrap"
                style={{
                  backgroundColor: `${scene.accentColor}08`,
                  color: scene.accentColor,
                  borderColor: `${scene.accentColor}25`,
                }}
              >
                {tag}
              </span>
            ))}
          </div>
          {scene.introImage && (
            <div className="mt-6">
              <img
                src={assetUrl(scene.introImage)}
                alt="方案简介"
                className="w-full rounded-xl border border-gray-100"
              />
            </div>
          )}
        </div>

        {/* Bottom: feature cards — responsive grid based on count */}
        <div className={gridClass}>
          {scene.features.map((feature) => (
            <div
              key={feature.title}
              className="p-5 rounded-xl border transition-all duration-200 hover:border-opacity-60 group"
              style={{ borderColor: `${scene.accentColor}18`, backgroundColor: `${scene.accentColor}04` }}
            >
              <div
                className="w-10 h-10 flex items-center justify-center rounded-lg mb-4 transition-transform duration-200 group-hover:scale-110"
                style={{ backgroundColor: `${scene.accentColor}12` }}
              >
                <i className={`${feature.icon} text-lg`} style={{ color: scene.accentColor }}></i>
              </div>
              <h4 className="text-sm font-bold text-[#0d1f3c] mb-2">{feature.title}</h4>
              <p className="text-xs text-gray-500 leading-relaxed">{feature.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
